import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import {
  View,
  TextInput,
  Text,
  StyleSheet,
  TouchableOpacity,
  FlatList,
  Animated,
  Keyboard,
  Platform,
} from 'react-native';
import { Search, X, MapPin, Plus } from 'lucide-react-native';
import { useTheme, SPACING, BORDER_RADIUS, ELEVATION, TYPOGRAPHY, withWeight } from '../../theme';
import { Structure } from '../../types';
import { useStructuresStore } from '../../store/structuresStore';
import { useClearButtonAnimation } from '@hooks/useAnimations';
import { useWebHover } from '@hooks/useWebHover';
import { CURSOR_POINTER } from '../../utils/webStyles';
import { useEscapeToClose } from '../../utils/escapeStack';
import { DotLabel } from '../common';

interface AddressSearchBarProps {
  onSelectStructure?: (structure: Structure) => void;
  onAddProperty?: (query: string) => void;
  placeholder?: string;
  currentAreaId?: string | null;
  currentAreaName?: string | null;
  /**
   * Desktop shell only (map screen, Sept 2026): the bar starts as a 44px
   * round icon button at the top-left of the map and expands to the full
   * field on click, "/", or an imperative `focus()`. Off (the default)
   * keeps the old always-expanded field — every phone/tablet call site.
   */
  collapsible?: boolean;
  /**
   * A standing request to focus the field (the map screen's
   * uiStore.mapSearchFocusRequested). The bar expands and focuses itself
   * while it's set, and calls `onFocusRequestHandled` once the input really
   * has focus, so a request made before the bar mounts, or across a
   * remount, still lands.
   */
  focusRequested?: boolean;
  onFocusRequestHandled?: () => void;
}

const resultDomId = (structureId: string) => `address-result-${structureId}`;

export interface AddressSearchBarHandle {
  clear: () => void;
  focus: () => void;
}

export const AddressSearchBar = forwardRef<AddressSearchBarHandle, AddressSearchBarProps>(
  function AddressSearchBar(
    {
      onSelectStructure,
      onAddProperty,
      placeholder = 'Search address...',
      currentAreaId = null,
      currentAreaName = null,
      collapsible = false,
      focusRequested = false,
      onFocusRequestHandled,
    }: AddressSearchBarProps,
    ref
  ) {
    const { colors } = useTheme();
    const [query, setQuery] = useState('');
    const [isFocused, setIsFocused] = useState(false);
    // Starts collapsed only when `collapsible` — every non-collapsible call
    // site (phone, tablet) is always "expanded" and never renders the
    // toggle button below.
    const [expanded, setExpanded] = useState(!collapsible);
    const { searchResults, isSearching, searchStructures, clearSearch, ensureAreaLoadedForSearch } =
      useStructuresStore();
    // Search reads only the home shards already in memory, so a query typed
    // while they are still arriving would stay empty forever. Re-run it as
    // more load (a cold phone start with ~90 shards loses that race).
    const loadedShardCount = useStructuresStore((s) => Object.keys(s._structuresByShard).length);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const blurTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const inputRef = useRef<TextInput>(null);
    const clearButtonAnim = useClearButtonAnimation(query.length > 0);
    const { hovered: toggleHovered, hoverProps: toggleHoverProps } = useWebHover();

    // A narrower viewport (phone/tablet) always wins: force expanded so a
    // shell resize away from the desktop layout can never strand the bar
    // collapsed with no toggle in reach.
    useEffect(() => {
      if (!collapsible) setExpanded(true);
    }, [collapsible]);

    // Focus once the field actually mounts (collapsed -> expanded flips a
    // render first, then this runs after commit). Collapsible only — a
    // non-collapsible bar (phone/tablet) is "expanded" from its very first
    // render, and this would otherwise steal focus on every mount.
    useEffect(() => {
      if (collapsible && expanded) inputRef.current?.focus();
    }, [collapsible, expanded]);

    // Standing focus request: expand (the effect above then focuses), or
    // focus directly when already expanded. Cleared from onFocus below.
    useEffect(() => {
      if (!focusRequested) return;
      if (collapsible && !expanded) setExpanded(true);
      else inputRef.current?.focus();
    }, [focusRequested, collapsible, expanded]);

    // "/" opens search the way it does in most map/table tools, as long as
    // the key isn't about to land in some other field.
    useEffect(() => {
      if (!collapsible || expanded || Platform.OS !== 'web') return;
      const onKeyDown = (e: KeyboardEvent) => {
        if (e.key !== '/') return;
        const active = document.activeElement as HTMLElement | null;
        const tag = active?.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || active?.isContentEditable) return;
        e.preventDefault();
        setExpanded(true);
      };
      document.addEventListener('keydown', onKeyDown);
      return () => document.removeEventListener('keydown', onKeyDown);
    }, [collapsible, expanded]);

    // Esc collapses the bar, like any other floating surface (see
    // src/utils/escapeStack) — but only while it's empty. With a query or a
    // selected address showing, Esc is a no-op here (it stays expanded) so
    // it can't be lost by a stray keystroke.
    useEscapeToClose(collapsible && expanded, () => {
      if (query.trim().length === 0) setExpanded(false);
    });

    // Clear search results + any pending timers on unmount so they can't outlive
    // the screen (stale state + potential post-unmount setState warnings).
    useEffect(() => {
      return () => {
        if (blurTimerRef.current) clearTimeout(blurTimerRef.current);
        clearSearch();
      };
    }, [clearSearch]);

    // Whether the most recent dispatched search was scoped to an area.
    // Drives the empty-state choice: scoped miss shows a "search other areas"
    // fallback button; global miss just says "No addresses found."
    const [lastScopedToAreaId, setLastScopedToAreaId] = useState<string | null>(null);

    // Search only scans shards already in `_structuresByShard`, and
    // loadAreasForViewport caps a cold load at MAX_SHARDS_PER_LOAD — a dense
    // area (Big Bear's 91 shards today) can leave dozens unloaded until the
    // user pans there. Fire-and-forget: never awaited in the UI path, and a
    // no-op above SEARCH_PRELOAD_MAX_STRUCTURES or once the area is fully
    // loaded (see ensureAreaLoadedForSearch's own idempotence).
    const preloadAreaForSearch = useCallback(() => {
      if (!currentAreaId) return;
      void ensureAreaLoadedForSearch(currentAreaId);
    }, [currentAreaId, ensureAreaLoadedForSearch]);

    useEffect(() => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      if (query.trim().length >= 2) {
        const areaId = currentAreaId ?? undefined;
        debounceRef.current = setTimeout(() => {
          setLastScopedToAreaId(areaId ?? null);
          searchStructures(query.trim(), areaId ? { areaId } : undefined);
          preloadAreaForSearch();
        }, 300);
      } else {
        setLastScopedToAreaId(null);
        clearSearch();
      }

      return () => {
        if (debounceRef.current) {
          clearTimeout(debounceRef.current);
        }
      };
    }, [
      query,
      currentAreaId,
      searchStructures,
      clearSearch,
      loadedShardCount,
      preloadAreaForSearch,
    ]);

    const handleSearchOtherAreas = useCallback(() => {
      const trimmed = query.trim();
      if (trimmed.length < 2) return;
      setLastScopedToAreaId(null);
      searchStructures(trimmed);
    }, [query, searchStructures]);

    useEffect(() => {
      if (query.length > 0) {
        clearButtonAnim.show();
      } else {
        clearButtonAnim.hide();
      }
    }, [query, clearButtonAnim]);

    const handleClear = useCallback(() => {
      setQuery('');
      clearSearch();
      Keyboard.dismiss();
    }, [clearSearch]);

    useImperativeHandle(
      ref,
      () => ({
        clear: () => {
          setQuery('');
          clearSearch();
        },
        focus: () => {
          // Collapsed: expand first — the effect above focuses the input
          // once it mounts. Already expanded: focus directly, same as
          // before this prop existed.
          if (collapsible && !expanded) {
            setExpanded(true);
          } else {
            inputRef.current?.focus();
          }
        },
      }),
      [clearSearch, collapsible, expanded]
    );

    const handleSelect = useCallback(
      (structure: Structure) => {
        setQuery(structure.address || '');
        clearSearch();
        Keyboard.dismiss();
        onSelectStructure?.(structure);
      },
      [clearSearch, onSelectStructure]
    );

    // Arrow keys move a highlight through the results; Enter picks it, or the
    // top result when none is highlighted. -1 means no highlight. Reset
    // whenever the results change, so it never points at a stale row.
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    useEffect(() => {
      setHighlightedIndex(-1);
    }, [searchResults]);
    const keyNavRef = useRef({ results: searchResults, highlighted: -1, select: handleSelect });
    keyNavRef.current = {
      results: searchResults,
      highlighted: highlightedIndex,
      select: handleSelect,
    };
    const keyNavActive =
      Platform.OS === 'web' && isFocused && !isSearching && searchResults.length > 0;
    // Capture phase, same reasoning as the command palette: react-native-web's
    // TextInput stops propagation on every keydown, so a bubble listener on
    // document never sees these while the caret is in the field.
    useEffect(() => {
      if (!keyNavActive) return;
      const onKeyDown = (e: KeyboardEvent) => {
        const { results, highlighted, select } = keyNavRef.current;
        if (results.length === 0) return;
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setHighlightedIndex(highlighted + 1 >= results.length ? 0 : highlighted + 1);
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          setHighlightedIndex(highlighted <= 0 ? results.length - 1 : highlighted - 1);
        } else if (e.key === 'Enter' && !e.isComposing) {
          e.preventDefault();
          e.stopPropagation();
          select(results[highlighted >= 0 ? highlighted : 0]);
        }
      };
      document.addEventListener('keydown', onKeyDown, true);
      return () => document.removeEventListener('keydown', onKeyDown, true);
    }, [keyNavActive]);

    // Also show the panel when the user has typed something but matched
    // nothing — that's where the "Add property" CTA lives, and previously
    // the panel hid the moment results turned empty.
    const hasTypedQuery = query.trim().length >= 2;
    const showResults = isFocused && (searchResults.length > 0 || isSearching || hasTypedQuery);

    // Collapsed: just the round toggle. Same surface/shadow treatment as
    // MapControls' floating buttons (ELEVATION.paper, hairline border,
    // surfaceElevated fill) so it reads as one family of map chrome with
    // the zoom/locate/layers stack.
    if (collapsible && !expanded) {
      return (
        <View style={[styles.container, styles.containerCollapsed]}>
          <TouchableOpacity
            {...toggleHoverProps}
            onPress={() => setExpanded(true)}
            style={[
              styles.toggleButton,
              { backgroundColor: colors.surfaceElevated, borderColor: colors.border },
              toggleHovered && { backgroundColor: colors.hoverSurface },
              ELEVATION.paper,
              CURSOR_POINTER,
            ]}
            accessibilityRole="button"
            accessibilityLabel="Search address"
            testID="address-search-toggle">
            <Search size={20} color={colors.text} />
          </TouchableOpacity>
        </View>
      );
    }

    return (
      <View style={[styles.container, collapsible && styles.containerExpanded]}>
        <View
          style={[
            styles.searchContainer,
            {
              backgroundColor: colors.surfaceElevated,
              borderColor: isFocused ? colors.borderFocus : colors.border,
            },
            ELEVATION.paper,
          ]}>
          <View style={styles.inputRow}>
            <Search
              size={20}
              color={isFocused ? colors.primary : colors.textTertiary}
              style={styles.searchIcon}
            />
            <TextInput
              ref={inputRef}
              style={[styles.input, { color: colors.text }]}
              placeholder={placeholder}
              placeholderTextColor={colors.textTertiary}
              value={query}
              onChangeText={setQuery}
              onFocus={() => {
                setIsFocused(true);
                if (focusRequested) onFocusRequestHandled?.();
                preloadAreaForSearch();
              }}
              onBlur={() => {
                if (blurTimerRef.current) clearTimeout(blurTimerRef.current);
                blurTimerRef.current = setTimeout(() => {
                  setIsFocused(false);
                  blurTimerRef.current = null;
                  // Collapse on blur, but only once the field is empty — a
                  // query in progress or a just-selected address keeps the
                  // bar expanded.
                  if (collapsible && query.trim().length === 0) {
                    setExpanded(false);
                  }
                }, 200);
              }}
              returnKeyType="search"
              autoCorrect={false}
              autoCapitalize="words"
              accessibilityLabel="Search address"
              accessibilityHint="Type at least two characters to search nearby properties"
              {...(keyNavActive && highlightedIndex >= 0 && searchResults[highlightedIndex]
                ? ({
                    'aria-activedescendant': resultDomId(searchResults[highlightedIndex].id),
                  } as object)
                : null)}
            />
            {query.length > 0 && (
              <Animated.View
                style={[
                  styles.clearButtonWrap,
                  {
                    opacity: clearButtonAnim.opacity,
                    transform: [{ scale: clearButtonAnim.scale }],
                  },
                ]}>
                <TouchableOpacity
                  onPress={handleClear}
                  style={styles.clearButton}
                  accessibilityRole="button"
                  accessibilityLabel="Clear search">
                  <X size={16} color={colors.textTertiary} />
                </TouchableOpacity>
              </Animated.View>
            )}
          </View>
        </View>

        {showResults && (
          <View
            style={[
              styles.resultsContainer,
              {
                backgroundColor: colors.surfaceElevated,
                borderColor: colors.border,
              },
              ELEVATION.md,
            ]}>
            {isSearching ? (
              <View style={styles.loadingContainer}>
                <Text style={[styles.loadingText, { color: colors.textSecondary }]}>
                  Searching...
                </Text>
              </View>
            ) : (
              <FlatList
                data={searchResults}
                keyExtractor={(item) => item.id}
                keyboardShouldPersistTaps="handled"
                extraData={highlightedIndex}
                renderItem={({ item, index }) => {
                  const claimedSuffix = item.claimSummary?.claimedBy ? ', already claimed' : '';
                  const a11yLabel =
                    `${item.address || 'Unknown address'}, ` +
                    `${item.city}, ${item.state}${item.zip ? ' ' + item.zip : ''}` +
                    claimedSuffix;
                  return (
                    <TouchableOpacity
                      style={[
                        styles.resultItem,
                        { borderBottomColor: colors.divider },
                        index === highlightedIndex && { backgroundColor: colors.hoverSurface },
                      ]}
                      id={resultDomId(item.id)}
                      onPress={() => handleSelect(item)}
                      accessibilityRole="button"
                      accessibilityLabel={a11yLabel}>
                      <MapPin size={16} color={colors.textSecondary} style={styles.resultIcon} />
                      <View style={styles.resultContent}>
                        <Text
                          style={[styles.resultAddress, { color: colors.text }]}
                          numberOfLines={1}>
                          {item.address || 'Unknown Address'}
                        </Text>
                        <Text
                          style={[styles.resultCity, { color: colors.textSecondary }]}
                          numberOfLines={1}>
                          {item.city}, {item.state} {item.zip}
                        </Text>
                      </View>
                      {item.claimSummary?.claimedBy && (
                        <DotLabel label="Claimed" variant="progress" />
                      )}
                    </TouchableOpacity>
                  );
                }}
                ListEmptyComponent={
                  lastScopedToAreaId && currentAreaName ? (
                    <View style={styles.emptyContainer}>
                      <Text style={[styles.emptyText, { color: colors.textSecondary }]}>
                        No matches in {currentAreaName}
                      </Text>
                      <TouchableOpacity
                        onPress={handleSearchOtherAreas}
                        style={styles.fallbackButton}
                        accessibilityRole="button"
                        accessibilityLabel="Search everywhere">
                        <Text style={[styles.fallbackButtonText, { color: colors.text }]}>
                          Search everywhere
                        </Text>
                      </TouchableOpacity>
                      {onAddProperty && (
                        <TouchableOpacity
                          onPress={() => {
                            const q = query.trim();
                            if (q.length === 0) return;
                            Keyboard.dismiss();
                            onAddProperty(q);
                          }}
                          style={[styles.addPropertyRow, { borderTopColor: colors.divider }]}
                          accessibilityRole="button"
                          accessibilityLabel="Add this property">
                          <Plus size={16} color={colors.textSecondary} style={styles.resultIcon} />
                          <View style={styles.resultContent}>
                            <Text
                              style={[styles.resultAddress, { color: colors.text }]}
                              numberOfLines={1}>
                              Don&apos;t see your property?
                            </Text>
                            <Text
                              style={[styles.resultCity, { color: colors.textSecondary }]}
                              numberOfLines={1}>
                              Add it as &quot;{query.trim()}&quot;
                            </Text>
                          </View>
                        </TouchableOpacity>
                      )}
                    </View>
                  ) : (
                    <View style={styles.emptyContainer}>
                      <Text style={[styles.emptyText, { color: colors.textTertiary }]}>
                        No addresses found
                      </Text>
                      {onAddProperty && (
                        <TouchableOpacity
                          onPress={() => {
                            const q = query.trim();
                            if (q.length === 0) return;
                            Keyboard.dismiss();
                            onAddProperty(q);
                          }}
                          style={[styles.addPropertyRow, { borderTopColor: colors.divider }]}
                          accessibilityRole="button"
                          accessibilityLabel="Add this property">
                          <Plus size={16} color={colors.textSecondary} style={styles.resultIcon} />
                          <View style={styles.resultContent}>
                            <Text
                              style={[styles.resultAddress, { color: colors.text }]}
                              numberOfLines={1}>
                              Don&apos;t see your property?
                            </Text>
                            <Text
                              style={[styles.resultCity, { color: colors.textSecondary }]}
                              numberOfLines={1}>
                              Add it as &quot;{query.trim()}&quot;
                            </Text>
                          </View>
                        </TouchableOpacity>
                      )}
                    </View>
                  )
                }
              />
            )}
          </View>
        )}
      </View>
    );
  }
);

const styles = StyleSheet.create({
  container: {
    position: 'relative',
    zIndex: 100,
  },
  // Desktop shell only — fixed widths so the invisible wrapper (position:
  // relative, otherwise happy to stretch to whatever its parent allows)
  // matches the round button or the field exactly, instead of the parent's
  // full width.
  containerCollapsed: {
    width: 44,
  },
  containerExpanded: {
    width: 360,
  },
  // Collapsed desktop toggle — same surface/shadow family as MapControls'
  // floating buttons (round instead of the stack's rounded-rect, since it
  // stands alone rather than sitting in a strip).
  toggleButton: {
    width: 44,
    height: 44,
    borderRadius: BORDER_RADIUS.full,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  // Plaud grammar: the map search is a soft-shadowed pill; the hairline
  // border thickens to ink only via color (width stays 1px on focus so the
  // geometry never jumps).
  searchContainer: {
    flexDirection: 'column',
    borderWidth: 1,
    borderRadius: BORDER_RADIUS.full,
  },
  inputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 46,
    paddingHorizontal: SPACING.smd,
  },
  searchIcon: {
    marginRight: SPACING.sm,
    flexShrink: 0,
  },
  clearButtonWrap: {
    flexShrink: 0,
  },
  input: {
    flex: 1,
    // minWidth: 0 is load-bearing on web — without it, a flex child in a row
    // won't shrink below its intrinsic content width, which was pushing the
    // clear button outside the rounded container when text got long.
    minWidth: 0,
    ...TYPOGRAPHY.body,
    height: '100%',
    outlineStyle: 'none' as any,
  },
  clearButton: {
    padding: SPACING.xs,
    marginLeft: SPACING.xs,
    flexShrink: 0,
  },
  resultsContainer: {
    position: 'absolute',
    top: '100%',
    marginTop: SPACING.xs,
    left: 0,
    right: 0,
    maxHeight: 280,
    borderWidth: 1,
    borderRadius: BORDER_RADIUS.lg,
    overflow: 'hidden',
  },
  loadingContainer: {
    padding: SPACING.lg,
    alignItems: 'center',
  },
  loadingText: {
    ...TYPOGRAPHY.body,
  },
  resultItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: SPACING.smd,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  // Bare icon, no tinted disc — the ListItem rule.
  resultIcon: {
    marginRight: SPACING.smd,
  },
  resultContent: {
    flex: 1,
  },
  resultAddress: {
    ...withWeight(TYPOGRAPHY.body, 'medium'),
  },
  resultCity: {
    ...TYPOGRAPHY.bodySmall,
    marginTop: 2,
  },
  emptyContainer: {
    padding: SPACING.lg,
    alignItems: 'center',
  },
  emptyText: {
    ...TYPOGRAPHY.body,
  },
  fallbackButton: {
    marginTop: SPACING.sm,
    paddingVertical: SPACING.xs,
    paddingHorizontal: SPACING.smd,
  },
  fallbackButtonText: {
    ...withWeight(TYPOGRAPHY.body, 'medium'),
  },
  addPropertyRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingTop: SPACING.smd,
    paddingHorizontal: SPACING.smd,
    paddingBottom: SPACING.smd,
    borderTopWidth: StyleSheet.hairlineWidth,
    width: '100%',
    marginTop: SPACING.smd,
  },
});
